import Matter from 'matter-js';
import { integrateCar, type VehicleParams } from './vehicle';
import { getVehicle } from './vehicles';
import { neutralInput, type CarInput, type CarState, type WorldState } from './types';
import { Track, type TrackDef, type Gate, type Vec2 } from './track';
import { RaceProgress } from './raceProgress';
import { Driver } from '../ai/Driver';
import { getAiProfile } from '../ai/profiles';
import type { WallSpec } from '../scenarios/types';

export interface WorldCarSpec {
  id: string;
  x: number;
  y: number;
  angle: number;
  /** Type de véhicule (clé du registre `VEHICLES`). Défaut : voiture. */
  vehicle?: string;
  /** Profil d'IA (clé de AI_PROFILES). Si défini ET qu'il y a une piste, la voiture est pilotée par l'IA. */
  ai?: string | null;
}

export interface WorldOptions {
  seed: number;
  cars: WorldCarSpec[];
  walls?: WallSpec[];
  track?: TrackDef;
  vehicle?: VehicleParams;
}

export interface WallView {
  x: number;
  y: number;
  w: number;
  h: number;
  angle: number;
}

/** Vue du circuit pour le rendu (la collision reste interne). */
export interface TrackView {
  centerline: Vec2[];
  inner: Vec2[];
  outer: Vec2[];
  gates: Gate[];
  halfWidth: number;
  laps: number;
}

/** Sommets RÉELS des corps Matter (vérité de la collision), pour l'overlay de debug. */
export interface CollisionShapes {
  cars: Vec2[][];
  walls: Vec2[][];
}

/**
 * Le monde simulé (matter-js autonome). Modèle hybride : contrôleur maison -> vélocité du
 * corps -> Matter intègre + résout les collisions. Depuis M3 il porte aussi le circuit
 * (murs d'enceinte générés depuis la centerline) et le suivi de course (tours, classement).
 */
export class World {
  step = 0;
  time = 0;
  readonly seed: number;
  readonly cars: CarState[];
  /** Conduite résolue PAR voiture (depuis son type), ou l'override global opts.vehicle. */
  private readonly carParams: VehicleParams[];
  private readonly engine: Matter.Engine;
  private readonly bodies: Matter.Body[];
  private readonly collisionBodies: Matter.Body[]; // murs décor + enceinte circuit
  private readonly wallViews: WallView[];
  private readonly track: Track | null;
  private readonly race: RaceProgress | null;
  /** Pilote IA par voiture (null = pilotée par les inputs passés). */
  private readonly drivers: (Driver | null)[];

  constructor(opts: WorldOptions) {
    this.seed = opts.seed;

    this.engine = Matter.Engine.create();
    this.engine.gravity.x = 0;
    this.engine.gravity.y = 0;
    this.engine.enableSleeping = false;

    // Résolution par voiture : géométrie + conduite depuis le type (opts.vehicle force tout le monde).
    const defs = opts.cars.map((c) => getVehicle(c.vehicle));
    this.carParams = defs.map((d) => opts.vehicle ?? d.params);
    const bodyDims = defs;
    this.bodies = opts.cars.map((c, i) => {
      const d = bodyDims[i];
      const body = Matter.Bodies.rectangle(c.x, c.y, d.w, d.h, {
        chamfer: { radius: d.chamfer },
        frictionAir: 0,
        friction: 0.02,
        frictionStatic: 0,
        restitution: WALL_RESTITUTION,
      });
      Matter.Body.setAngle(body, c.angle);
      return body;
    });

    // Murs décor explicites (scénario).
    const decorWalls = opts.walls ?? [];
    this.wallViews = decorWalls.map((w) => ({ x: w.x, y: w.y, w: w.w, h: w.h, angle: w.angle ?? 0 }));
    this.collisionBodies = decorWalls.map((w) => {
      const body = Matter.Bodies.rectangle(w.x, w.y, w.w, w.h, {
        isStatic: true,
        friction: 0.02,
        restitution: WALL_RESTITUTION,
      });
      if (w.angle) Matter.Body.setAngle(body, w.angle);
      return body;
    });

    // Circuit : enceinte intérieure + extérieure générées depuis la centerline.
    // Les murs sont décalés vers l'extérieur pour que leur face côté piste coïncide
    // avec la bordure visuelle (sinon on cogne avant la bordure — cf. boundaryWalls).
    this.track = opts.track ? new Track(opts.track) : null;
    if (this.track) {
      const wl = this.track.boundaryWalls(WALL_THICKNESS);
      this.collisionBodies.push(...segmentWalls(wl.inner, WALL_THICKNESS));
      this.collisionBodies.push(...segmentWalls(wl.outer, WALL_THICKNESS));
    }

    Matter.Composite.add(this.engine.world, [...this.bodies, ...this.collisionBodies]);

    this.cars = opts.cars.map((c, i) => ({
      id: c.id,
      w: bodyDims[i].w,
      h: bodyDims[i].h,
      x: c.x,
      y: c.y,
      angle: c.angle,
      vx: 0,
      vy: 0,
      speed: 0,
      angularVelocity: 0,
      steerAngle: 0,
      lateralSlip: 0,
      grip: 1,
      lap: 0,
      checkpointIdx: -1,
      penetration: 0,
      position: 1,
      wrongWay: false,
      lastLapTime: 0,
      bestLapTime: 0,
    }));

    this.race = this.track ? new RaceProgress(this.track, this.cars) : null;

    // Un pilote IA par voiture qui a un profil (et s'il y a une piste à suivre).
    const track = this.track;
    this.drivers = opts.cars.map((c, i) =>
      c.ai && track ? new Driver(getAiProfile(c.ai), track, { x: c.x, y: c.y }, opts.seed * 31 + i + 1) : null,
    );
  }

  /** Points de visée courants des IA (pour l'overlay racing line) ; null si pas d'IA. */
  getAiTargets(): (Vec2 | null)[] {
    return this.drivers.map((d) => (d ? { ...d.lookAheadTarget } : null));
  }

  fixedUpdate(dt: number, inputs: CarInput[]): void {
    for (let i = 0; i < this.cars.length; i++) {
      const car = this.cars[i];
      const body = this.bodies[i];
      car.angle = body.angle;
      car.vx = body.velocity.x / dt;
      car.vy = body.velocity.y / dt;

      // Input : l'IA calcule le sien (état courant + piste + voitures voisines), sinon input passé.
      const driver = this.drivers[i];
      const input =
        driver && this.track
          ? driver.computeInput(car, this.track, otherCars(this.cars, i))
          : inputs[i] ?? neutralInput();

      // `penetration` vient de la fin du pas précédent : s'il touche un mur, on effondre
      // le grip ce pas pour que le rebond ne soit pas annulé (anti-scotch au mur).
      const contact = car.penetration > WALL_CONTACT_EPS;
      integrateCar(car, input, dt, this.carParams[i], contact);

      Matter.Body.setVelocity(body, { x: car.vx * dt, y: car.vy * dt });
      Matter.Body.setAngle(body, car.angle);
      Matter.Body.setAngularVelocity(body, 0);
    }

    Matter.Engine.update(this.engine, dt * 1000);

    for (let i = 0; i < this.cars.length; i++) {
      const car = this.cars[i];
      const body = this.bodies[i];
      car.x = body.position.x;
      car.y = body.position.y;
      car.angle = body.angle;
      car.vx = body.velocity.x / dt;
      car.vy = body.velocity.y / dt;
      car.speed = Math.hypot(car.vx, car.vy);
      car.penetration = this.penetrationOf(body);
    }

    this.step += 1;
    this.time += dt;

    this.race?.update(this.cars, this.time);
  }

  private penetrationOf(body: Matter.Body): number {
    if (this.collisionBodies.length === 0) return 0;
    let max = 0;
    for (const c of Matter.Query.collides(body, this.collisionBodies)) {
      if (c.depth > max) max = c.depth;
    }
    return max;
  }

  getWalls(): WallView[] {
    return this.wallViews.map((w) => ({ ...w }));
  }

  /** Sommets monde des corps de collision (voitures + murs) — vérité de la physique. */
  getCollisionShapes(): CollisionShapes {
    const verts = (b: Matter.Body): Vec2[] => b.vertices.map((v) => ({ x: v.x, y: v.y }));
    return { cars: this.bodies.map(verts), walls: this.collisionBodies.map(verts) };
  }

  getTrack(): TrackView | null {
    if (!this.track) return null;
    return {
      centerline: this.track.samples.map((s) => ({ x: s.x, y: s.y })),
      inner: this.track.inner.map((p) => ({ ...p })),
      outer: this.track.outer.map((p) => ({ ...p })),
      gates: this.track.gates.map((g) => ({ ...g })),
      halfWidth: this.track.halfWidth,
      laps: this.track.laps,
    };
  }

  getState(): WorldState {
    return {
      step: this.step,
      time: this.time,
      cars: this.cars.map((c) => ({ ...c })),
    };
  }

  hash(): string {
    const r = (n: number) => Math.round(n * 1000) / 1000;
    const body = this.cars
      .map((c) => `${c.id}:${r(c.x)},${r(c.y)},${r(c.angle)},${r(c.vx)},${r(c.vy)}`)
      .join('|');
    return `${body}#${this.step}`;
  }
}

const WALL_THICKNESS = 18;
/**
 * Rebond des collisions (voiture + murs). Plus haut = la voiture rebondit, ne reste pas collée.
 * Le modèle hybride atténue ~⅓ (la traînée mange une part du rebond au pas suivant), donc on
 * règle un peu au-dessus du ressenti voulu : 0.5 ≈ rebond effectif ~0.33.
 */
const WALL_RESTITUTION = 0.5;
/** Pénétration (px) au-delà de laquelle on considère la voiture en contact d'un mur. */
const WALL_CONTACT_EPS = 0.05;

/** Toutes les voitures SAUF celle d'index `self` (perception de l'IA). */
function otherCars(cars: CarState[], self: number): CarState[] {
  const out: CarState[] = [];
  for (let j = 0; j < cars.length; j++) if (j !== self) out.push(cars[j]);
  return out;
}

/** Corps-murs statiques entre points consécutifs d'une polyligne fermée. */
function segmentWalls(points: Vec2[], thickness: number): Matter.Body[] {
  const bodies: Matter.Body[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-3) continue;
    const body = Matter.Bodies.rectangle((a.x + b.x) / 2, (a.y + b.y) / 2, len + 4, thickness, {
      isStatic: true,
      friction: 0.02,
      restitution: WALL_RESTITUTION,
    });
    Matter.Body.setAngle(body, Math.atan2(dy, dx));
    bodies.push(body);
  }
  return bodies;
}
